import {
    AppLayout,
    AppLayoutProps,
    Button,
    Container,
    GridProps,
    SpaceBetween,
    TextContent,
} from "@amzn/awsui-components-react";
import CustomGridLayout from "common/component/CustomGridLayout/CustomGridLayout";
import CustomPopOver from "common/component/CustomPopOver/CustomPopOver";
import CustomPropertyFilter from "common/component/CustomPropertyFilter/CustomPropertyFilter";
import CheckboxFilter from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";
import OrderItemsTable from "common/component/OrderItemsTable/OrderItemsTable";
import NavigationBreadcrumbs from "common/navigation/NavigationBreadcrumbs";
import Paths from "common/paths/paths";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { LOCAL_IT_ORDER_LIST } from "helpers";
import useFlashbar from "hooks/useFlashbar";
import useOrderResponse from "hooks/useOrderResponse";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useOrderFiltering from "./useOrderFiltering";

const getStringPath: string = "views.itOrderList";

/**
 * Renders the filtering section for the order list.
 * Combines search, requester filtering, status filters, and location filters.
 *
 * @param orderFiltering - Object containing all filtering state and handlers
 * @returns JSX element with all filter controls
 */
const OrderListFiltering = ({ orderFiltering }: { orderFiltering: ReturnType<typeof useOrderFiltering> }) => {
    return (
        <Container>
            <SpaceBetween size="m">
                <CustomPropertyFilter
                    label={getString(`${getStringPath}.searchOrders.label`)}
                    placeholder={getString(`${getStringPath}.searchOrders.placeholder`)}
                    testId={LOCAL_IT_ORDER_LIST.SEARCH_FOR}
                    onFilterChange={({ detail }) => orderFiltering.filterOptions.setQuery(detail)}
                    query={orderFiltering.filterOptions.query}
                    filteringProperties={orderFiltering.filterOptions.filteringProperties}
                    hideOperations={false}
                    filteringOptions={orderFiltering.filterOptions.categoryOptions}
                    touchpoint="intake-search-orders"
                />
                <CustomPropertyFilter
                    label={getString(`${getStringPath}.requester.label`)}
                    placeholder={getString(`${getStringPath}.requester.placeholder`)}
                    testId={LOCAL_IT_ORDER_LIST.REQUESTER}
                    onFilterChange={({ detail }) => orderFiltering.filterOptions.setRequesterQuery(detail)}
                    query={orderFiltering.filterOptions.requesterQuery}
                    filteringProperties={orderFiltering.filterOptions.requesterFilteringProperty}
                    hideOperations={true}
                    info={
                        <CustomPopOver
                            content={<TextContent>{getString(`${getStringPath}.requester.info`)}</TextContent>}
                        />
                    }
                    touchpoint="intake-search-requesters"
                />
                <CheckboxFilter
                    name={getString(`${getStringPath}.filters.orderStatus`)}
                    checkBoxStructure={orderFiltering.filterOptions.orderStatus}
                    setCheckBoxStructure={orderFiltering.filterOptions.setOrderStatus}
                    testId={LOCAL_IT_ORDER_LIST.STATUS_FILTERS}
                    touchpoint="intake-order-status"
                />
                <CheckboxFilter
                    name={getString(`${getStringPath}.filters.locations`)}
                    description={getString(`${getStringPath}.filters.locationsDescription`)}
                    checkBoxStructure={orderFiltering.filterOptions.locationSelectedItems}
                    setCheckBoxStructure={orderFiltering.filterOptions.setLocationSelectedItems}
                    testId={LOCAL_IT_ORDER_LIST.LOCATION_FILTERS}
                    touchpoint="intake-order-locations"
                />
            </SpaceBetween>
        </Container>
    );
};

/**
 * Main component for the Local IT Order List view.
 * Provides comprehensive order filtering, searching, and management interface.
 *
 * @returns JSX element containing the complete order list interface
 */
const LocalITOrderList = () => {
    /* Order Created Notification */
    const { Flashbar, generateMessage, generateError } = useFlashbar(true);

    useOrderResponse(generateMessage);

    /* Filtering */
    const orderFiltering = useOrderFiltering();

    /**
     * Navigates to the hardware restock page for creating a new order.
     */
    const navigate = useNavigate();

    const navigateToNewOrder = () => {
        navigate(Paths.itHardwareRestock.url);
    };

    /* APP LAYOUT CONFIGURATION */
    const contentType: AppLayoutProps.ContentType = "table";

    /* GRID DEFINITION */
    const gridDefinition: ReadonlyArray<GridProps.ElementDefinition> = [
        { colspan: { default: 3, xl: 2 } },
        { colspan: { default: 9, xl: 10 } },
    ];

    /* BREADCRUMBS */
    const breadcrumbs = {
        items: [{ text: Paths.itOrderList.name, href: Paths.itOrderList.url }],
    };

    /** Track total orders */
    const [totalOrders, setTotalOrders] = useState(0);

    return (
        <AppLayout
            navigationHide={true}
            toolsHide={true}
            notifications={Flashbar}
            breadcrumbs={<NavigationBreadcrumbs items={breadcrumbs.items} />}
            content={
                <CustomGridLayout
                    title={getString(`${getStringPath}.title`)}
                    counter={`(${totalOrders})`}
                    actions={
                        <Button
                            variant="primary"
                            onClick={navigateToNewOrder}
                            data-testid={LOCAL_IT_ORDER_LIST.NEW_ORDER}
                        >
                            {getString(`${getStringPath}.newOrderButton`)}
                        </Button>
                    }
                    leftSection={<OrderListFiltering orderFiltering={orderFiltering} />}
                    rightSection={
                        <OrderItemsTable
                            generateError={generateError}
                            query={orderFiltering.filter.query}
                            isLoading={orderFiltering.filter.filterLoading}
                            sorting={orderFiltering.sorting.currentSorting}
                            onSortingChange={orderFiltering.sorting.onSortingChange}
                            setTotalOrders={setTotalOrders}
                        />
                    }
                    gridDefinition={gridDefinition}
                />
            }
            contentType={contentType}
        />
    );
};

export default LocalITOrderList;
